import { FunctionComponent, ReactElement, useMemo, useState } from 'react';
import { Customer, SingleSite, UserStatistics } from '@/core/entities';
import { ALL_SITES_ID, useMostActiveUsers } from '@/queries/logs';
import { Column } from '@/components/ui/table/types';
import { MostActiveUserResponse } from '@/api/dashboard/types';
import { Typography } from '@mui/material';
import StatisticsTable from './StatisticsTable';
import { QueryDateRange } from '../../types';
import { formatNumber, getPercent, getPercentColor } from '../../utils';
import { SelectedLocation } from '../completed-cycles/filters/LocationFiltersDialog';

const MostActiveUsers: FunctionComponent<{
	site?: SingleSite;
	locationsFilter?: SelectedLocation[];
	locationId?: string;
	customer?: Pick<Customer, 'id' | 'name'>;
	showAllSitesData: boolean;
	isAdmin: boolean;
	dateRange: QueryDateRange;
}> = ({ site, locationId, customer, showAllSitesData, isAdmin, dateRange }): ReactElement => {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const params: Record<string, any> = {
		fromDate: dateRange.start,
		toDate: dateRange.end,
	};

	if (customer) {
		params.customerId = customer.id;
	}

	if (locationId) {
		params.locationId = locationId;
	}
	const { query: mostActiveUsersQuery } = useMostActiveUsers(
		params,
		showAllSitesData ? ALL_SITES_ID : site?.id
	);

	const isLoading = useMemo(
		() => mostActiveUsersQuery.isFetching,
		[mostActiveUsersQuery.isFetching]
	);

	const [viewMode, setViewMode] = useState<'completed' | 'error'>('completed');

	const tableColumns = useMemo(() => {
		const columns: Column<MostActiveUserResponse>[] = [
			{
				name: 'User',
				render: ({ customer_name, site_name, user_firstName, user_lastName }) => (
					<div>
						<div>
							{user_firstName} {user_lastName}
						</div>
						{isAdmin ? <small>{site_name || customer_name}</small> : <small>{site_name}</small>}
					</div>
				),
				accessors: ['customer_name', 'site_name', 'user_firstName', 'user_lastName'],
			},
			{
				name: 'Cycles',
				accessors: ['count'],
				align: 'right',
				render: ({ count }) => formatNumber(count),
			},
			{
				name: 'Completed',
				accessors: ['complete_count'],
				align: 'right',
				render: ({ complete_count }) => formatNumber(complete_count),
			},
			{
				name: '%',
				render: ({ count = 0, complete_count = 0 }) => {
					const completionPercent = getPercent(count, complete_count);
					const errorRate = 100 - completionPercent;
					const color = getPercentColor(viewMode === 'error' ? errorRate : completionPercent, viewMode === 'error');
					return <Typography sx={{ color }}>{formatNumber(completionPercent)}%</Typography>;
				},
				accessors: ['count', 'complete_count'],
				align: 'right',
				minWidth: 80,
			},
		];
		return columns;
	}, [isAdmin, viewMode]);

	const dataTransform = (items: MostActiveUserResponse[]) => {
		if (viewMode === 'completed') return items;
		return [...items].sort((a, b) => {
			const errorRateA = 100 - getPercent(a.count, a.complete_count);
			const errorRateB = 100 - getPercent(b.count, b.complete_count);
			return errorRateB - errorRateA;
		});
	};

	const sx = {
		'@media print': {
			margin: '20px',
			boxShadow: 'none',
			pageBreakInside: 'avoid',
			breakInside: 'avoid',
		},
	};

	return (
		<StatisticsTable<UserStatistics>
			title="Users"
			query={mostActiveUsersQuery}
			columns={tableColumns}
			emptyState="No users available"
			dataTransform={dataTransform}
			showViewButtons
			onViewChange={setViewMode}
			viewMode={viewMode}
			sx={sx}
			isLoading={isLoading}
		/>
	);
};

export default MostActiveUsers;
